import { Button, Card, Col, DatePicker, Form, Input, Row, Select, Space, theme } from 'antd';
import { useEffect, useState } from 'react';
import { computeButtonColLayout, filterFormGridLayout, filterFormItemLayout, filterFormItemStyle } from '@utils/property';
import dayjs from 'dayjs';
import './LaborProgressFilterPanel.less';

const VISIBLE_FIELD_COUNT = 4;
const TOTAL_FIELD_COUNT = 17;

export default function LaborProgressFilterPanel({ filter, onFinish, onReset }) {
    const { token } = theme.useToken();
    const [form] = Form.useForm();
    const [expanded, setExpanded] = useState(false);
    const buttonColLayout = computeButtonColLayout(expanded ? TOTAL_FIELD_COUNT : VISIBLE_FIELD_COUNT);

    useEffect(() => {
        const formValues = { ...filter };
        // 处理日期字段
        if (filter.startTime && filter.endTime) {
            try {
                formValues.dates = [dayjs.isDayjs(filter.startTime) ? filter.startTime : dayjs(filter.startTime), dayjs.isDayjs(filter.endTime) ? filter.endTime : dayjs(filter.endTime)];
            } catch {
                delete formValues.dates;
            }
        }
        // 处理项目编号字段
        if (filter.ids && Array.isArray(filter.ids) && filter.ids.length > 0) {
            formValues.ids = filter.ids.map(id => `YHY${String(id).padStart(4, '0')}`).join(',');
        }
        form.setFieldsValue(formValues);

        // 如果有展开字段的值，自动展开
        const hasExpandedValues =
            (filter.h3yunNo !== undefined && filter.h3yunNo !== '')
            || filter.conferenceStatus !== undefined
            || (filter.isTesting !== undefined && filter.isTesting !== 0)
            || filter.node1 !== undefined
            || filter.node2 !== undefined
            || filter.node3 !== undefined
            || filter.node4 !== undefined
            || filter.node5 !== undefined
            || filter.node6 !== undefined
            || filter.node7 !== undefined
            || filter.node8 !== undefined
            || filter.node9 !== undefined
            || filter.node10 !== undefined;
        if (hasExpandedValues) {
            setExpanded(true);
        }
    }, [filter, form]);

    const handleReset = () => {
        form.resetFields();
        setExpanded(false);
        onReset();
    };

    const handleFinish = values => {
        const newValues = { ...values };
        // 处理日期字段
        if (values.dates && values.dates.length === 2) {
            newValues.startTime = values.dates[0].format('YYYY-MM-DD');
            newValues.endTime = values.dates[1].format('YYYY-MM-DD');
        } else {
            newValues.startTime = undefined;
            newValues.endTime = undefined;
        }
        delete newValues.dates;
        // 处理项目编号字段
        if (values.ids) {
            try {
                const idListStr = values.ids?.replace(/YHY0+/gi, '') || '';
                const idList = idListStr.split(',') || [];
                if (idList.length) {
                    newValues.ids = idList.map(id => parseInt(id, 10) || 0).filter(id => id > 0);
                }
            } catch {
                newValues.ids = [];
            }
        }
        onFinish(newValues);
    };

    return (
        <Card
            size="small"
            className="labor-progress-filter-panel"
            style={{
                marginBottom: token.marginLG,
                border: `1px solid ${token.colorBorderSecondary}`,
                background: token.colorFillAlter,
            }}
        >
            <Form {...filterFormItemLayout} form={form} onFinish={handleFinish} autoComplete="off" requiredMark={false}>
                <Row gutter={[16, 16]}>
                    <Col {...filterFormGridLayout}>
                        <Form.Item label="项目名称" name="name" style={filterFormItemStyle}>
                            <Input allowClear placeholder="会议项目名称关键字" />
                        </Form.Item>
                    </Col>

                    <Col {...filterFormGridLayout}>
                        <Form.Item label="会议项目编号" name="ids" style={filterFormItemStyle}>
                            <Input allowClear placeholder="会议项目编号或ID" />
                        </Form.Item>
                    </Col>

                    <Col {...filterFormGridLayout}>
                        <Form.Item label="氚云项目编号" name="h3yunNo" style={filterFormItemStyle}>
                            <Input allowClear placeholder="氚云项目编号" />
                        </Form.Item>
                    </Col>

                    <Col {...filterFormGridLayout}>
                        <Form.Item label="项目执行日期" name="dates" style={filterFormItemStyle} className="conferencelist_date">
                            <DatePicker.RangePicker style={{ width: '100%' }} />
                        </Form.Item>
                    </Col>

                    {expanded && (
                        <>
                            <Col {...filterFormGridLayout}>
                                <Form.Item label="项目创建人" name="createdBy" style={filterFormItemStyle}>
                                    <Input allowClear placeholder="项目创建人" />
                                </Form.Item>
                            </Col>

                            <Col {...filterFormGridLayout}>
                                <Form.Item name="conferenceStatus" label="项目执行进度" style={filterFormItemStyle}>
                                    <Select placeholder="请选择" allowClear>
                                        <Select.Option value={0}>未开始</Select.Option>
                                        <Select.Option value={1}>进行中</Select.Option>
                                        <Select.Option value={2}>已完成</Select.Option>
                                    </Select>
                                </Form.Item>
                            </Col>

                            <Col {...filterFormGridLayout}>
                                <Form.Item name="node1" label="经办人发起" style={filterFormItemStyle}>
                                    <Select placeholder="请选择" allowClear>
                                        <Select.Option value={1}>待提交</Select.Option>
                                        <Select.Option value={2}>已提交</Select.Option>
                                    </Select>
                                </Form.Item>
                            </Col>

                            <Col {...filterFormGridLayout}>
                                <Form.Item name="node2" label="部门负责人审核" style={filterFormItemStyle}>
                                    <Select placeholder="请选择" allowClear>
                                        <Select.Option value={0}>未开始</Select.Option>
                                        <Select.Option value={1}>待审核</Select.Option>
                                        <Select.Option value={2}>同意</Select.Option>
                                    </Select>
                                </Form.Item>
                            </Col>

                            <Col {...filterFormGridLayout}>
                                <Form.Item name="node3" label="分管高管审批" style={filterFormItemStyle}>
                                    <Select placeholder="请选择" allowClear>
                                        <Select.Option value={0}>未开始</Select.Option>
                                        <Select.Option value={1}>待审批</Select.Option>
                                        <Select.Option value={2}>同意</Select.Option>
                                    </Select>
                                </Form.Item>
                            </Col>

                            <Col {...filterFormGridLayout}>
                                <Form.Item name="node4" label="总裁审批" style={filterFormItemStyle}>
                                    <Select placeholder="请选择" allowClear>
                                        <Select.Option value={0}>未开始</Select.Option>
                                        <Select.Option value={1}>待审批</Select.Option>
                                        <Select.Option value={2}>同意</Select.Option>
                                    </Select>
                                </Form.Item>
                            </Col>

                            <Col {...filterFormGridLayout}>
                                <Form.Item name="node5" label="财务经理审核" style={filterFormItemStyle}>
                                    <Select placeholder="请选择" allowClear>
                                        <Select.Option value={0}>未开始</Select.Option>
                                        <Select.Option value={1}>待审核</Select.Option>
                                        <Select.Option value={2}>同意</Select.Option>
                                    </Select>
                                </Form.Item>
                            </Col>

                            <Col {...filterFormGridLayout}>
                                <Form.Item name="node6" label="财务总监审批" style={filterFormItemStyle}>
                                    <Select placeholder="请选择" allowClear>
                                        <Select.Option value={0}>未开始</Select.Option>
                                        <Select.Option value={1}>待审批</Select.Option>
                                        <Select.Option value={2}>同意</Select.Option>
                                    </Select>
                                </Form.Item>
                            </Col>

                            <Col {...filterFormGridLayout}>
                                <Form.Item name="node7" label="付款申请" style={filterFormItemStyle}>
                                    <Select placeholder="请选择" allowClear>
                                        <Select.Option value={0}>未开始</Select.Option>
                                        <Select.Option value={1}>待提交</Select.Option>
                                        <Select.Option value={2}>部分提交</Select.Option>
                                        <Select.Option value={3}>全部提交</Select.Option>
                                    </Select>
                                </Form.Item>
                            </Col>

                            <Col {...filterFormGridLayout}>
                                <Form.Item name="node8" label="付款申请审批" style={filterFormItemStyle}>
                                    <Select placeholder="请选择" allowClear>
                                        <Select.Option value={1}>未开始</Select.Option>
                                        <Select.Option value={2}>待审批</Select.Option>
                                        <Select.Option value={3}>同意</Select.Option>
                                    </Select>
                                </Form.Item>
                            </Col>

                            <Col {...filterFormGridLayout}>
                                <Form.Item name="node9" label="经办人提交单据" style={filterFormItemStyle}>
                                    <Select placeholder="请选择" allowClear>
                                        <Select.Option value={0}>未开始</Select.Option>
                                        <Select.Option value={1}>待提交</Select.Option>
                                        <Select.Option value={3}>全部提交</Select.Option>
                                    </Select>
                                </Form.Item>
                            </Col>

                            <Col {...filterFormGridLayout}>
                                <Form.Item name="node10" label="出纳付款" style={filterFormItemStyle}>
                                    <Select placeholder="请选择" allowClear>
                                        <Select.Option value={0}>未开始</Select.Option>
                                        <Select.Option value={1}>待付款</Select.Option>
                                        <Select.Option value={3}>全部付款</Select.Option>
                                    </Select>
                                </Form.Item>
                            </Col>

                            <Col {...filterFormGridLayout}>
                                <Form.Item label="正式/测试" name="isTesting" style={filterFormItemStyle}>
                                    <Select placeholder="是否测试项目" allowClear>
                                        <Select.Option value={0}>正式项目</Select.Option>
                                        <Select.Option value={1}>测试项目</Select.Option>
                                    </Select>
                                </Form.Item>
                            </Col>
                        </>
                    )}

                    <Col {...buttonColLayout}>
                        <Form.Item label={<span>&nbsp;</span>} colon={false} style={filterFormItemStyle}>
                            <Space size="small">
                                <Button type="default" onClick={handleReset}>
                                    重置
                                </Button>
                                <Button type="primary" htmlType="submit" icon={<i className="bi bi-search" aria-hidden="true" />}>
                                    检索
                                </Button>
                                {TOTAL_FIELD_COUNT > VISIBLE_FIELD_COUNT && (
                                    <Button type="link" size="small" aria-expanded={expanded} onClick={() => setExpanded(prev => !prev)} style={{ fontSize: 12, color: token.colorTextSecondary }}>
                                        {expanded ? (
                                            <>
                                                收起 <span className="bi bi-chevron-up" aria-hidden="true" />
                                            </>
                                        ) : (
                                            <>
                                                展开 <span className="bi bi-chevron-down" aria-hidden="true" />
                                            </>
                                        )}
                                    </Button>
                                )}
                            </Space>
                        </Form.Item>
                    </Col>
                </Row>
            </Form>
        </Card>
    );
}
